import {
  ArrowDownLeft,
  ArrowUpRight,
  CalendarClock,
  Database,
  HardDrive,
  Hourglass,
  Image,
  MessageSquare,
  MessagesSquare,
  Users,
} from "lucide-react";

import type { CompanyUsageRow } from "../api/usage.service";
import { formatarBytes } from "./formatarBytes";

interface UsageOverviewProps {
  rows: CompanyUsageRow[];
  isLoading: boolean;
  dias: number;
}

const nf = new Intl.NumberFormat("pt-BR");

interface Indicador {
  rotulo: string;
  valor: string;
  legenda?: string;
  icone: React.ElementType;
}

function Bloco({
  titulo,
  cor,
  indicadores,
  carregando,
}: {
  titulo: string;
  cor: string;
  indicadores: Indicador[];
  carregando: boolean;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <h3 className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {titulo}
      </h3>
      <div className="grid gap-4 sm:grid-cols-2">
        {indicadores.map((ind) => (
          <div key={ind.rotulo} className="flex items-start gap-3">
            <div className={`shrink-0 rounded-lg p-2 ${cor}`}>
              <ind.icone className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500">{ind.rotulo}</p>
              {carregando ? (
                <div className="mt-1 h-5 w-20 animate-pulse rounded bg-gray-200" />
              ) : (
                <>
                  <p className="text-lg font-bold leading-tight tabular-nums text-gray-900">
                    {ind.valor}
                  </p>
                  {ind.legenda && (
                    <p className="text-xs text-gray-400">{ind.legenda}</p>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Visão geral do período, agrupada por natureza do consumo.
 *
 * **Por que categorizar em vez de enfileirar cartões.** São dez números. Numa
 * fileira única, "mensagens enviadas" fica visualmente do lado de "espaço em
 * disco" sem que uma coisa tenha a ver com a outra. Agrupados, cada bloco
 * responde uma pergunta: quanto tráfego, quanto atendimento, quanto espaço.
 *
 * A ordem dos blocos segue o que puxa custo: tráfego primeiro (é o que cresce
 * com uso), espaço por último (é o que acumula).
 */
export function UsageOverview({ rows, isLoading, dias }: UsageOverviewProps) {
  const t = rows.reduce(
    (acc, r) => ({
      mensagens: acc.mensagens + r.messagesTotal,
      recebidas: acc.recebidas + r.messagesIn,
      enviadas: acc.enviadas + r.messagesOut,
      midia: acc.midia + r.mediaCount,
      conversas: acc.conversas + r.conversationsActive,
      conversasNovas: acc.conversasNovas + r.conversationsNew,
      contatos: acc.contatos + r.contactsTotal,
      contatosNovos: acc.contatosNovos + r.contactsNew,
      agendamentos: acc.agendamentos + r.scheduledTotal,
      enviados: acc.enviados + r.scheduledSent,
      aguardando: acc.aguardando + r.scheduledPending,
      blob: acc.blob + r.storageBytes,
      arquivos: acc.arquivos + r.storageFiles,
      banco: acc.banco + r.dbBytes,
    }),
    {
      mensagens: 0,
      recebidas: 0,
      enviadas: 0,
      midia: 0,
      conversas: 0,
      conversasNovas: 0,
      contatos: 0,
      contatosNovos: 0,
      agendamentos: 0,
      enviados: 0,
      aguardando: 0,
      blob: 0,
      arquivos: 0,
      banco: 0,
    },
  );

  const pct = (parte: number) =>
    t.mensagens > 0
      ? `${((parte / t.mensagens) * 100).toFixed(0)}% do total`
      : undefined;

  return (
    <div className="grid gap-4 xl:grid-cols-3">
      <Bloco
        titulo={`Mensagens · ${dias} dias`}
        cor="bg-violet-50 text-violet-600"
        carregando={isLoading}
        indicadores={[
          {
            rotulo: "Total",
            valor: nf.format(t.mensagens),
            icone: MessageSquare,
          },
          {
            rotulo: "Recebidas",
            valor: nf.format(t.recebidas),
            legenda: pct(t.recebidas),
            icone: ArrowDownLeft,
          },
          {
            rotulo: "Enviadas",
            valor: nf.format(t.enviadas),
            legenda: pct(t.enviadas),
            icone: ArrowUpRight,
          },
          {
            rotulo: "Com mídia",
            valor: nf.format(t.midia),
            legenda: pct(t.midia),
            icone: Image,
          },
        ]}
      />

      <Bloco
        titulo="Atendimento"
        cor="bg-emerald-50 text-emerald-600"
        carregando={isLoading}
        indicadores={[
          {
            rotulo: "Conversas ativas",
            valor: nf.format(t.conversas),
            legenda: `${nf.format(t.conversasNovas)} abertas no período`,
            icone: MessagesSquare,
          },
          {
            rotulo: "Contatos",
            valor: nf.format(t.contatos),
            legenda: `${nf.format(t.contatosNovos)} novos no período`,
            icone: Users,
          },
          {
            rotulo: "Agendamentos",
            valor: nf.format(t.agendamentos),
            legenda: `${nf.format(t.enviados)} já disparados`,
            icone: CalendarClock,
          },
          {
            rotulo: "Aguardando disparo",
            valor: nf.format(t.aguardando),
            legenda: "ainda na fila",
            icone: Hourglass,
          },
        ]}
      />

      <Bloco
        titulo="Espaço ocupado · agora"
        cor="bg-amber-50 text-amber-600"
        carregando={isLoading}
        indicadores={[
          {
            rotulo: "Arquivos",
            valor: formatarBytes(t.blob),
            legenda: `${nf.format(t.arquivos)} no Blob Storage`,
            icone: HardDrive,
          },
          {
            rotulo: "Banco de dados",
            valor: formatarBytes(t.banco),
            legenda: "estimado",
            icone: Database,
          },
        ]}
      />
    </div>
  );
}
